import { useState, useCallback, type ReactNode } from "react"
import { usePage } from "@inertiajs/react"
import type { SharedProps } from "@/types"
import { useAccountChannel } from "@/hooks/use-account-channel"

/**
 * Wraps a user avatar with an SVG credit arc ring (ElevenLabs-style).
 * The arc shows remaining credits as a proportion of the last top-up.
 */
export function AvatarCreditRing({
  children,
  size = 36,
}: {
  children: ReactNode
  size?: number
}) {
  const { currentAccount } = usePage<SharedProps>().props
  const [liveBalance, setLiveBalance] = useState<number | null>(null)

  useAccountChannel(
    currentAccount?.id,
    useCallback((balance: number) => setLiveBalance(balance), []),
  )

  if (!currentAccount) {
    return <div className="relative shrink-0" style={{ width: size, height: size }}>{children}</div>
  }

  const balance = liveBalance ?? currentAccount.credit_balance
  const peak = currentAccount.peak_credit_balance
  const percentage = peak > 0
    ? Math.max(0, Math.min(100, (balance / peak) * 100))
    : balance > 0 ? 100 : 0
  const isLow = peak > 0 && balance / peak < 0.15

  const strokeWidth = 2.5
  const gap = 2.5
  const radius = size / 2 + gap
  const svgSize = (radius + strokeWidth / 2) * 2
  const center = svgSize / 2
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (percentage / 100) * circumference

  const displayBalance = balance < 0 ? 0 : balance
  const compact = (n: number) => {
    if (n >= 10_000) return `${(n / 1000).toFixed(0)}k`
    if (n >= 1_000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}k`
    return n.toLocaleString()
  }

  return (
    <div className="group/credit relative shrink-0" style={{ width: svgSize, height: svgSize }}>
      <svg
        width={svgSize}
        height={svgSize}
        viewBox={`0 0 ${svgSize} ${svgSize}`}
        className="absolute inset-0 -rotate-90"
      >
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          className="stroke-stone/50"
        />
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          className={`transition-[stroke-dashoffset] duration-700 ease-out ${isLow ? "stroke-amber-500" : "stroke-red"}`}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
      {/* Avatar — hides on hover */}
      <div
        className="absolute transition-opacity duration-150 group-hover/credit:opacity-0"
        style={{
          top: (svgSize - size) / 2,
          left: (svgSize - size) / 2,
          width: size,
          height: size,
        }}
      >
        {children}
      </div>
      {/* Credit balance — shows on hover */}
      <span
        className="absolute inset-0 flex items-center justify-center font-semibold tabular-nums leading-none text-charcoal opacity-0 transition-opacity duration-150 group-hover/credit:opacity-100"
        style={{ fontSize: size <= 32 ? 9 : 10 }}
      >
        {compact(displayBalance)}
      </span>
    </div>
  )
}

/** Standalone CreditRing for the credits page hero */
export function CreditRing({
  percentage,
  label,
  size = 40,
  strokeWidth: sw,
  low = false,
}: {
  percentage: number
  label: string
  size?: number
  strokeWidth?: number
  low?: boolean
}) {
  const strokeWidth = sw ?? 2.5
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (percentage / 100) * circumference
  const fontSize = label.length > 4 ? 8 : label.length > 3 ? 9 : 10

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          className="stroke-stone"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          className={`transition-[stroke-dashoffset] duration-700 ease-out ${low ? "stroke-amber-500" : "stroke-red"}`}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
      <span
        className="absolute inset-0 flex items-center justify-center font-semibold tabular-nums leading-none text-charcoal"
        style={{ fontSize }}
      >
        {label}
      </span>
    </div>
  )
}
